import React from "react";
import { Check, X, ArrowRight, Lightbulb } from "lucide-react";

const Title = ({ children, color }) => <div className="text-xs font-bold uppercase tracking-[0.18em] mb-2" style={{ color: color || "var(--ink-soft)" }}>{children}</div>;

const P = ({ b }) => <p className="text-[var(--ink)] leading-relaxed">{b.text}</p>;
const Sub = ({ b }) => <p className="text-[var(--ink)] font-semibold leading-relaxed">{b.text}</p>;

const List = ({ b }) => (
  <div>
    {b.title && <Title>{b.title}</Title>}
    <ul className={`grid gap-x-6 gap-y-2 ${b.cols === 2 ? "sm:grid-cols-2" : ""}`}>
      {b.items.map((it) => <li key={it} className="flex items-start gap-2.5 text-[var(--ink)] leading-snug"><span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ background: "var(--gold)" }} />{it}</li>)}
    </ul>
  </div>
);

const Keep = ({ b }) => (
  <div className="g-tint rounded-2xl border-l-4 p-4 sm:p-5 flex gap-3" style={{ background: "#FFF7E6", borderColor: "#E39A4C", "--part": "#E39A4C" }}>
    <Lightbulb size={22} className="shrink-0 mt-0.5" style={{ color: "#E39A4C" }} />
    <div><div className="font-bold text-[var(--ink)] mb-1">{b.title}</div><p className="text-[var(--ink)] leading-relaxed">{b.text}</p></div>
  </div>
);

const FavorAvoid = ({ b, good }) => (
  <div className={`rounded-2xl p-4 sm:p-5 ${good ? "g-good" : "g-bad"}`} style={{ background: good ? "#E7F5EE" : "#FDE9E5" }}>
    <div className="flex items-center gap-2 font-bold mb-3" style={{ color: good ? "#2F7A55" : "#B4483A" }}>
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: good ? "#2F7A55" : "#B4483A" }}>{good ? <Check size={16} strokeWidth={3} /> : <X size={16} strokeWidth={3} />}</span>
      {b.title || (good ? "À favoriser" : "À éviter")}
    </div>
    <ul className={`grid gap-x-6 gap-y-2 ${b.items.length > 5 ? "sm:grid-cols-2" : ""}`}>
      {b.items.map((it) => <li key={it} className="flex items-start gap-2 text-[var(--ink)] leading-snug"><span className="mt-0.5 shrink-0 font-bold" style={{ color: good ? "#2F7A55" : "#B4483A" }}>{good ? "✓" : "✗"}</span>{it}</li>)}
    </ul>
  </div>
);

const Steps = ({ b, color }) => (
  <div>
    {b.title && <Title color={color}>{b.title}</Title>}
    <ol className="space-y-2.5">
      {b.items.map((s, i) => (
        <li key={i} className="flex gap-3 rounded-2xl bg-white border border-[var(--border)] p-3.5">
          <span className="w-9 h-9 rounded-full text-white font-bold flex items-center justify-center shrink-0 text-sm" style={{ background: color || "var(--gold)" }}>{b.numbered ? i + 1 : <ArrowRight size={16} />}</span>
          <div className="min-w-0"><div className="font-bold text-[var(--ink)]">{s.title}</div><div className="text-[var(--ink-soft)] leading-snug">{s.text}</div></div>
        </li>
      ))}
    </ol>
  </div>
);

const Flow = ({ b, color }) => (
  <div>
    {b.title && <Title color={color}>{b.title}</Title>}
    <div className="flex flex-wrap items-center gap-y-2">
      {b.items.map((it, i) => (
        <React.Fragment key={it}>
          <span className="px-4 py-2 rounded-full font-bold text-sm text-white shadow-sm" style={{ background: color || "var(--gold)", opacity: 0.75 + (0.25 * i) / Math.max(1, b.items.length - 1) }}>{it}</span>
          {i < b.items.length - 1 && <ArrowRight size={18} className="mx-1.5 shrink-0" style={{ color: color || "var(--gold)" }} />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const Example = ({ b }) => (
  <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
    <div className="g-bad rounded-2xl p-4 border-2" style={{ borderColor: "#F3C7BF", background: "#FFF5F3" }}>
      <div className="flex items-center gap-2 font-bold text-sm mb-2" style={{ color: "#B4483A" }}><X size={16} strokeWidth={3} /> {b.badTitle || "Plutôt que"}</div>
      {b.bad.map((t) => <p key={t} className="text-[var(--ink)] leading-snug italic">{t}</p>)}
    </div>
    <div className="hidden sm:flex items-center"><ArrowRight size={26} className="text-[var(--gold)]" /></div>
    <div className="g-good rounded-2xl p-4 border-2" style={{ borderColor: "#BFE3D2", background: "#F2FAF6" }}>
      <div className="flex items-center gap-2 font-bold text-sm mb-2" style={{ color: "#2F7A55" }}><Check size={16} strokeWidth={3} /> {b.goodTitle || "Préférer"}</div>
      <ol className="space-y-1">{b.good.map((t, i) => <li key={t} className="flex gap-2 text-[var(--ink)] leading-snug"><span className="font-bold shrink-0" style={{ color: "#2F7A55" }}>{b.good.length > 1 ? `${i + 1}.` : ""}</span>{t}</li>)}</ol>
    </div>
  </div>
);

const Quotes = ({ b, color }) => (
  <div>
    {b.title && <Title color={color}>{b.title}</Title>}
    <div className="flex flex-wrap gap-2">
      {b.items.map((q) => <span key={q} className="rounded-2xl rounded-bl-sm bg-white border border-[var(--border)] px-4 py-2.5 text-[var(--ink)] leading-snug shadow-sm">{q}</span>)}
    </div>
  </div>
);

const Levels = ({ b, color }) => (
  <div>
    {b.title && <Title color={color}>{b.title}</Title>}
    <div className="space-y-1.5">
      {b.items.map((it, i) => (
        <div key={it} className="flex items-center gap-3">
          <span className="text-xs font-bold text-[var(--ink-soft)] w-16 shrink-0">Niveau {i + 1}</span>
          <div className="h-10 rounded-r-full rounded-l-md flex items-center px-4 font-semibold text-white text-sm" style={{ width: `${40 + (60 * (i + 1)) / b.items.length}%`, background: color || "var(--gold)", opacity: 0.6 + (0.4 * i) / (b.items.length - 1) }}>{it}</div>
        </div>
      ))}
    </div>
  </div>
);

const Table = ({ b }) => (
  <div className="overflow-hidden rounded-2xl border border-[var(--border)]">
    <table className="w-full text-[var(--ink)]">
      <thead><tr className="bg-[var(--sand)] text-left text-xs uppercase tracking-wider">{b.head.map((h) => <th key={h} className="px-4 py-2.5 font-bold">{h}</th>)}</tr></thead>
      <tbody>{b.rows.map((r) => <tr key={r[0]} className="border-t border-[var(--border)] bg-white"><td className="px-4 py-2.5 font-semibold whitespace-nowrap">{r[0]}</td><td className="px-4 py-2.5 text-[var(--ink-soft)]">{r[1]}</td></tr>)}</tbody>
    </table>
  </div>
);

const CheckList = ({ b }) => (
  <div className="rounded-2xl bg-white border border-[var(--border)] p-4">
    {b.title && <div className="font-bold text-[var(--ink)] mb-2">{b.title}</div>}
    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">{b.items.map((it) => <li key={it} className="flex items-center gap-2.5 text-[var(--ink)]"><span className="w-5 h-5 rounded border-2 border-[var(--gold)] shrink-0" />{it}</li>)}</ul>
  </div>
);

const RENDER = { p: P, sub: Sub, list: List, keep: Keep, favor: (p) => <FavorAvoid {...p} good />, avoid: (p) => <FavorAvoid {...p} />, steps: Steps, flow: Flow, example: Example, quotes: Quotes, levels: Levels, table: Table, check: CheckList };

export const Blocks = ({ blocks, color }) => (
  <div className="space-y-5">{blocks.map((b, i) => { const C = RENDER[b.t]; return C ? <C key={i} b={b} color={color} /> : null; })}</div>
);
